/**
 * Zitadel OIDC redirect-back handler.
 *
 * Mounted at the path `/desktop/auth/callback`. Receives `?code=…&state=…` from
 * Zitadel after the user signs in (or registers, or resets password) on the
 * hosted UI. Exchanges the code for tokens via PKCE, then redirects back to
 * the main app shell.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { handleZitadelCallback } from '@/lib/zitadel-oidc';

interface AuthCallbackProps {
  onSuccess: () => void;
}

export function AuthCallback({ onSuccess }: AuthCallbackProps) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    handleZitadelCallback(params)
      .then(() => {
        // Strip ?code= etc. from the URL and redirect to the app shell.
        window.history.replaceState({}, '', '/desktop/');
        onSuccess();
      })
      .catch((err) => {
        setError((err as Error).message);
      });
  }, [onSuccess]);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background text-foreground">
      <div className="max-w-md text-center space-y-3">
        <img
          src="/desktop/laabha.png"
          alt="Laabha"
          className="h-10 w-auto mx-auto"
          draggable={false}
        />
        {!error ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          </>
        ) : (
          <>
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive rounded p-3">
              {error}
            </div>
            <button
              onClick={() => (window.location.href = '/desktop/')}
              className="px-3 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
